import React, { FC, useEffect, useMemo } from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFetch, useStudentsByGroup } from '../../../../../shared/hooks';
import apiRoutes from '../../../../../../config/api/routes';
import { ListItem, ListItemWrapper } from '../../../../../shared/components';
import { Block } from '../../../../../../config/api/models/Block';
import { getGroupVideoProgressMapper } from '../../../../../../config/api/models';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import {
  AverageRow,
  AverageTitle,
  Col,
  Row,
  StyledProfilePicture,
  UserName,
  VideoProgressWrapper,
} from './Atoms';

type Props = {
  block: Block;
};

const VideoGroupProgress: FC<Props> = ({ block }) => {
  const { t } = useTranslation();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const refresh = useAppSelector((state) => state.filters.refresh);
  const { data: students} = useStudentsByGroup(selectedGroup || '', selectedGroup !== undefined && selectedGroup !== '');

  const [getGroupVideoProgressResponse, getGroupVideoProgress] = useFetch('', {
    mapper: getGroupVideoProgressMapper,
  });

  useEffect(() => {
    if (!selectedGroup) return;
    if (!block.content) return;
    getGroupVideoProgress({
      path: apiRoutes.groupVideoProgress(selectedGroup, block.id),
    });
  }, [selectedGroup, getGroupVideoProgress, block.content, refresh, block.id]);

  const averageProgress = useMemo(() => {
    if (!getGroupVideoProgressResponse.success) return '';
    if (students === undefined) return '';
    if (students.data.length === 0) return '';

    const data = getGroupVideoProgressResponse.data;

    let result = 0;
    for (let i = 0; i < data.length; i++) {
      result += data[i].played;
    }
    return `${Math.round((result * 100) / students.data.length)}%`;
  }, [getGroupVideoProgressResponse, students]);

  const studentList = useMemo(() => {
    if (students === undefined) return [];

    if (selectedStudent === '') {
      return students.data;
    } else {
      return students.data.filter((student) => student.id === selectedStudent);
    }
  }, [students, selectedStudent]);

  return !!studentList && !!students && getGroupVideoProgressResponse.success ? (
    <VideoProgressWrapper>
      {studentList.length === 0 ? (
        <>{t('trajectory.sections.shared.video.noResults')}</>
      ) : (
        <>
          <Row>
            <Col>
              <Typography variant="h3">
                {t('trajectory.sections.shared.video.lastStarted')}
              </Typography>
            </Col>
            <Col>
              <Typography variant="h3">{t('trajectory.sections.shared.video.progress')}</Typography>
            </Col>
          </Row>
          {studentList.map((student) => {
            const video = getGroupVideoProgressResponse.data.find(
              (video) => video.userId === student.id
            );
            return (
              <ListItemWrapper key={student.id}>
                <ListItem className="listItem">
                  <Row>
                    <UserName>
                      <StyledProfilePicture size="small" initials={student.initials} />
                      {`${student.fullName}`}
                    </UserName>
                    <Col>{!!video ? video.lastWatched : '/'}</Col>
                    <Col>{!!video ? Math.round(video.played * 100) + '%' : '0%'}</Col>
                  </Row>
                </ListItem>
              </ListItemWrapper>
            );
          })}
          <AverageRow>
            <AverageTitle variant="h3">
              {t('trajectory.sections.shared.answers.groupAverage')}
            </AverageTitle>
            <Col className="average">{averageProgress}</Col>
          </AverageRow>
        </>
      )}
    </VideoProgressWrapper>
  ) : null;
};

export default VideoGroupProgress;
